import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { TID } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Zap, LogIn, Loader2 } from "lucide-react";

const DEMO = [
  { label: "Super Admin", email: "admin@rushgro.com", password: "Admin@12345", tid: TID.demoAdminBtn, color: "from-blue-500 to-indigo-600" },
  { label: "Brand (RushX)", email: "brand@chaipoint.example", password: "Brand@12345", tid: TID.demoBrandBtn, color: "from-emerald-500 to-teal-600" },
  { label: "Outlet", email: "outlet@chaipoint.example", password: "Outlet@12345", tid: TID.demoOutletBtn, color: "from-amber-500 to-orange-600" },
  { label: "RushServ", email: "engineer@rushserv.example", password: "Service@12345", tid: TID.demoServBtn, color: "from-fuchsia-500 to-pink-600" },
  { label: "Manufacturer", email: "mfr@amaron.example", password: "Mfg@12345", tid: TID.demoMfrBtn, color: "from-slate-600 to-zinc-700" },
];

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("admin@rushgro.com");
  const [password, setPassword] = useState("Admin@12345");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  if (user) return <Navigate to={loc.state?.from?.pathname || "/dashboard"} replace />;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(""); setLoading(true);
    try {
      await login(email.trim(), password);
      nav(loc.state?.from?.pathname || "/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const fillDemo = (d) => { setEmail(d.email); setPassword(d.password); setErr(""); };

  return (
    <div className="min-h-screen w-full bg-background text-foreground relative overflow-hidden">
      {/* subtle grid + gradient background */}
      <div className="absolute inset-0 -z-10 opacity-[0.35]"
           style={{
             backgroundImage: "radial-gradient(circle at 20% 20%, hsl(217 91% 60% / 0.15), transparent 40%), radial-gradient(circle at 80% 60%, hsl(180 90% 55% / 0.12), transparent 40%)"
           }} />
      <div className="absolute inset-0 -z-10"
           style={{ backgroundImage: "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.25 }} />

      <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col min-h-screen">
        <header className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600 grid place-items-center">
            <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-heading text-lg font-semibold tracking-tight">RushGro</div>
          <span className="text-xs uppercase tracking-widest text-muted-foreground ml-1">PBaaS</span>
        </header>

        <div className="grid md:grid-cols-2 gap-10 flex-1 items-center mt-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-600 font-medium">Power Backup as a Service</div>
            <h1 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-semibold tracking-tight text-foreground">
              Uptime infrastructure for <span className="rushgro-gradient-text">every outlet.</span>
            </h1>
            <p className="mt-4 text-muted-foreground text-sm max-w-md">
              Subscribe to FlexiPod battery backup instead of buying. RushGro orchestrates manufacturers, assets, maintenance, warranty and billing — end-to-end.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              {[
                { k: "10K+", v: "Outlets Ready" },
                { k: "99.9%", v: "Uptime SLA" },
                { k: "24×7", v: "RushServ" },
              ].map((s) => (
                <div key={s.k} className="rounded-lg border bg-card p-3">
                  <div className="text-xl font-heading font-semibold">{s.k}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <Card className="p-6 md:p-8 rounded-xl border shadow-sm">
            <h2 className="font-heading text-xl font-semibold">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Use your RushGro credentials </p>
            <form className="mt-6 space-y-4" onSubmit={submit}>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid={TID.loginEmail} value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" data-testid={TID.loginPassword} value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="current-password" required />
              </div>
              {err && <div data-testid={TID.loginError} className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{err}</div>}
              <Button type="submit" data-testid={TID.loginSubmit} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : <><LogIn className="w-4 h-4 mr-2" /> Sign in</>}
              </Button>
            </form>
            {/* <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Demo Portals</div>
              <div className="grid grid-cols-2 gap-2">
                {DEMO.map((d) => (
                  <button
                    key={d.label}
                    data-testid={d.tid}
                    onClick={() => fillDemo(d)}
                    className={`text-left rounded-lg border p-2.5 hover:border-blue-500 transition-colors bg-card`}
                    type="button"
                  >
                    <div className={`text-xs font-medium bg-gradient-to-r ${d.color} bg-clip-text text-transparent`}>{d.label}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{d.email}</div>
                  </button>
                ))}
              </div>
            </div> */}
          </Card>
        </div>
      </div>
    </div>
  );
}
